/**
 * 产品选择模态框组件
 * 参考 BatchTransactionDrawer 的产品选择区域实现
 * 支持表格和分组两种视图模式，支持多选产品并设置数量
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Table, Card, Switch, Checkbox, Typography, Space, Button, Empty, message, Input } from "antd";
import { TableOutlined, AppstoreOutlined, TeamOutlined, EnvironmentOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Search } = Input;

const EMPTY_ARRAY = [];

// 样式定义
const styles = `
.product-selector-modal .selected-row {
    background-color: #f6ffed !important;
    border: 1px solid #52c41a;
}

.product-selector-modal .selected-row:hover {
    background-color: #f6ffed !important;
}

.product-selector-modal .ant-table-tbody > tr:hover > td {
    background-color: #e6f7ff;
}

.product-selector-modal .ant-table-tbody > tr > td {
    padding: 8px 12px !important;
    vertical-align: top;
}

.product-selector-modal .ant-table-thead > tr > th {
    padding: 8px 12px !important;
}
`;

/**
 * 产品选择模态框
 * @param {boolean} visible - 模态框是否可见
 * @param {function} onCancel - 取消回调
 * @param {function} onConfirm - 确认回调，参数为选中的产品列表 [{id, quantity, ...productInfo}]
 * @param {string} title - 模态框标题
 * @param {Array} availableItems - 可选的产品列表
 * @param {Array} selectedFlights - 选中的航班列表（用于显示上下文）
 * @param {string} transactionType - 事务类型
 * @param {string} serviceType - 服务类型
 * @param {string} mode - 模式：CREATE(新增) 或 EDIT(编辑)
 * @param {Array} initialSelectedProducts - 初始选中的产品（编辑模式使用）
 * @param {number} selectedSupplierId - 选中的供应商ID，用于筛选产品
 */
const ProductSelectorModal = ({ visible, onCancel, onConfirm, title = "选择产品", availableItems = [], selectedFlights = [], transactionType, serviceType, mode = "CREATE", initialSelectedProducts = EMPTY_ARRAY, selectedSupplierId = null }) => {
    // 视图模式：table(表格) 或 grouped(分组) - 默认使用卡片式
    const [viewMode, setViewMode] = useState("grouped");

    // 选中的产品及其数量 {productId: {product: productInfo, quantity: number}}
    const [selectedProducts, setSelectedProducts] = useState({});

    // 搜索和过滤
    const [searchText, setSearchText] = useState("");

    // 初始化选中的产品
    useEffect(() => {
        if (mode === "EDIT" && initialSelectedProducts.length > 0) {
            const productMap = {};
            initialSelectedProducts.forEach((item) => {
                productMap[item.id] = {
                    product: item,
                    quantity: item.quantity || 1,
                };
            });
            setSelectedProducts(productMap);
        } else {
            setSelectedProducts({});
        }
    }, [mode, initialSelectedProducts, visible]);

    // 过滤后的产品列表
    const filteredContractItems = useMemo(() => {
        let filtered = availableItems;

        // 供应商筛选
        if (selectedSupplierId !== null) {
            filtered = filtered.filter((item) => {
                const itemSupplierId = item.partner_id || item.supplier_id;
                return itemSupplierId === selectedSupplierId;
            });
        }

        // 搜索过滤
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((item) => item.product_name?.toLowerCase().includes(searchLower) || item.partner_name?.toLowerCase().includes(searchLower) || item.product_description?.toLowerCase().includes(searchLower));
        }

        return filtered;
    }, [availableItems, searchText, selectedSupplierId]);

    // 按供应商分组的产品
    const groupedContractItems = useMemo(() => {
        const groups = {};
        filteredContractItems.forEach((item) => {
            const supplierId = item.partner_id || item.supplier_id;
            const supplierName = item.partner_name || item.supplier_name || "未知供应商";

            if (!groups[supplierId]) {
                groups[supplierId] = {
                    supplier: { id: supplierId, name: supplierName },
                    items: [],
                };
            }
            groups[supplierId].items.push(item);
        });
        return groups;
    }, [filteredContractItems]);

    // 处理产品选择/取消选择
    const handleProductToggle = useCallback((product, selected) => {
        console.log("ProductSelectorModal: 切换产品选择", product.product_name, selected);
        setSelectedProducts((prev) => {
            const newSelected = { ...prev };
            if (selected) {
                newSelected[product.id] = {
                    product,
                    quantity: 1,
                };
            } else {
                delete newSelected[product.id];
            }
            console.log("ProductSelectorModal: 更新后的选择状态", newSelected);
            return newSelected;
        });
    }, []);

    // 处理数量变更 - 暂时注释，默认数量为1
    // const handleQuantityChange = useCallback((productId, quantity) => {
    //     setSelectedProducts((prev) => ({
    //         ...prev,
    //         [productId]: {
    //             ...prev[productId],
    //             quantity: quantity || 0,
    //         },
    //     }));
    // }, []);

    // 处理确认
    const handleConfirm = useCallback(() => {
        const selectedList = Object.values(selectedProducts)
            .filter((item) => item.quantity > 0)
            .map((item) => ({
                ...item.product,
                quantity: item.quantity,
            }));

        if (selectedList.length === 0) {
            message.warning("请至少选择一个产品");
            return;
        }

        console.log("ProductSelectorModal: 确认选择的产品", selectedList);
        onConfirm(selectedList);
    }, [selectedProducts, onConfirm]);

    // 表格列定义
    const contractItemColumns = [
        {
            title: "选择",
            key: "selection",
            width: 60,
            render: (_, record) => (
                <Checkbox
                    checked={!!selectedProducts[record.id]}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleProductToggle(record, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
        },
        {
            title: "产品信息",
            key: "product_info",
            width: 180,
            render: (_, record) => (
                <div style={{ lineHeight: "1.4" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 2 }}>{record.product_name}</div>
                    {record.product_description && (
                        <div
                            style={{
                                fontSize: 12,
                                color: "#666",
                                marginBottom: 2,
                                maxWidth: "320px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {record.product_description}
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: "#999" }}>单位: {record.product_unit || "件"}</div>
                </div>
            ),
        },
        {
            title: "供应商",
            dataIndex: "partner_name",
            key: "partner_name",
            width: 100,
        },
        // 结算模块还没上线，暂时不显示价格
        // {
        //     title: "单价",
        //     key: "price",
        //     width: 120,
        //     render: (_, record) => (
        //         <div>
        //             <Text strong>{record.price}</Text>
        //             <div style={{ fontSize: 12, color: "#666" }}>{record.currency}</div>
        //         </div>
        //     ),
        // },
        // 结算模块还没上线，暂时注释掉小计列
        // {
        //     title: "小计",
        //     key: "subtotal",
        //     width: 120,
        //     render: (_, record) => {
        //         const quantity = selectedProducts[record.id]?.quantity || 0;
        //         if (quantity === 0) return <Text type="secondary">-</Text>;

        //         const subtotal = (record.price * quantity).toFixed(2);
        //         return (
        //             <div>
        //                 <Text strong>{subtotal}</Text>
        //                 <div style={{ fontSize: 12, color: "#666" }}>{record.currency}</div>
        //             </div>
        //         );
        //     },
        // },
        {
            title: "适用范围",
            key: "applicability",
            width: 160,
            render: (_, record) => (
                <div>
                    {(record.applicable_airports || record.product?.default_applicable_airports) && (
                        <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            机场: {(record.applicable_airports || record.product?.default_applicable_airports || []).slice(0, 2).join(", ")}
                            {(record.applicable_airports || record.product?.default_applicable_airports || []).length > 2 && "..."}
                        </div>
                    )}
                    {(record.applicable_aircraft_types || record.product?.default_applicable_aircraft_types) && (
                        <div style={{ fontSize: 11, color: "#666" }}>
                            <Text type="secondary">
                                机型: {(record.applicable_aircraft_types || record.product?.default_applicable_aircraft_types || []).slice(0, 2).join(", ")}
                                {(record.applicable_aircraft_types || record.product?.default_applicable_aircraft_types || []).length > 2 && "..."}
                            </Text>
                        </div>
                    )}
                </div>
            ),
        },
        // {
        //     title: "数量",
        //     key: "quantity",
        //     width: 120,
        //     render: (_, record) => (selectedProducts[record.id] ? <InputNumber size="small" min={1} value={selectedProducts[record.id]?.quantity || 1} onChange={(value) => handleQuantityChange(record.id, value)} style={{ width: 80 }} /> : <Text type="secondary">-</Text>),
        // },
    ];

    // 渲染航班上下文信息
    const renderFlightContext = () => {
        if (!selectedFlights || selectedFlights.length === 0) return null;

        return (
            <Card
                size="small"
                style={{
                    marginBottom: 16,
                    backgroundColor: "#f6ffed",
                    border: "1px solid #b7eb8f",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <Text strong>选中航班: </Text>
                        <Text>{selectedFlights.length} 个航班</Text>
                    </div>
                    <div>
                        <Text type="secondary">事务类型: {transactionType}</Text>
                    </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                    {selectedFlights
                        .slice(0, 3)
                        .map((flight) => `${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport})`)
                        .join(", ")}
                    {selectedFlights.length > 3 && ` 等${selectedFlights.length}个航班`}
                </div>
            </Card>
        );
    };

    // 渲染搜索和过滤区域
    const renderSearchAndFilters = () => (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
                <Search placeholder="搜索产品名称、供应商或描述" value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ width: "100%" }} allowClear />

                {/* 已选择产品统计 */}
                {Object.keys(selectedProducts).length > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Text type="secondary">已选择 {Object.keys(selectedProducts).length} 个产品</Text>
                        <Button size="small" onClick={() => setSelectedProducts({})}>
                            清空选择
                        </Button>
                    </div>
                )}
            </Space>
        </Card>
    );

    // 渲染表格视图
    const renderTableView = () => (
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <Table
                dataSource={filteredContractItems}
                columns={contractItemColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 350 }}
                bordered
                rowClassName={(record) => {
                    if (selectedProducts[record.id]) {
                        return "selected-row";
                    }
                    return "";
                }}
                onRow={(record) => ({
                    onClick: () => {
                        const isSelected = !!selectedProducts[record.id];
                        handleProductToggle(record, !isSelected);
                    },
                    style: { cursor: "pointer" },
                })}
            />
        </div>
    );

    // 渲染分组视图
    const renderGroupedView = () => (
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {Object.entries(groupedContractItems).map(([supplierId, group]) => (
                <Card
                    key={supplierId}
                    size="small"
                    title={
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <TeamOutlined style={{ color: "#1890ff" }} />
                                <Text strong>{group.supplier.name}</Text>
                                <Text type="secondary">({group.items.length} 个产品)</Text>
                            </div>
                            <Checkbox
                                checked={group.items.every((item) => selectedProducts[item.id])}
                                indeterminate={group.items.some((item) => selectedProducts[item.id]) && !group.items.every((item) => selectedProducts[item.id])}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        const newSelected = { ...selectedProducts };
                                        group.items.forEach((item) => {
                                            if (!newSelected[item.id]) {
                                                newSelected[item.id] = {
                                                    product: item,
                                                    quantity: 1,
                                                };
                                            }
                                        });
                                        setSelectedProducts(newSelected);
                                    } else {
                                        const newSelected = { ...selectedProducts };
                                        group.items.forEach((item) => {
                                            delete newSelected[item.id];
                                        });
                                        setSelectedProducts(newSelected);
                                    }
                                }}
                            >
                                全选
                            </Checkbox>
                        </div>
                    }
                    style={{ marginBottom: 12 }}
                >
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 8 }}>
                        {group.items.map((item) => {
                            const isSelected = !!selectedProducts[item.id];
                            return (
                                <div
                                    key={item.id}
                                    style={{
                                        padding: 12,
                                        border: `1px solid ${isSelected ? "#52c41a" : "#d9d9d9"}`,
                                        borderRadius: 6,
                                        backgroundColor: isSelected ? "#f6ffed" : "#fafafa",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => handleProductToggle(item, !isSelected)}
                                >
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                        <Checkbox
                                            checked={isSelected}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleProductToggle(item, e.target.checked);
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            {/* 结算系统还没上线，暂时不显示价格 */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <Text strong>{item.product_name}</Text>
                                                {/* <div style={{ textAlign: "right" }}>
                                                    <Text strong>
                                                        {item.price} {item.currency}
                                                    </Text>
                                                </div> */}
                                            </div>

                                            {item.product_description && <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{item.product_description}</div>}

                                            {/* <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>单位: {item.product_unit || "件"}</div> */}

                                            {/* 数量选择 - 暂时隐藏，默认数量为1 */}
                                            {/* {isSelected && (
                                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                                    <Text style={{ fontSize: 12 }}>数量:</Text>
                                                    <InputNumber size="small" min={1} value={selectedProducts[item.id]?.quantity || 1} onChange={(value) => handleQuantityChange(item.id, value)} style={{ width: 80 }} onClick={(e) => e.stopPropagation()} />
                                                    <Text style={{ fontSize: 12, color: "#666" }}>
                                                        小计: {((selectedProducts[item.id]?.quantity || 1) * item.price).toFixed(2)} {item.currency}
                                                    </Text>
                                                </div>
                                            )} */}

                                            {/* 适用性信息 */}
                                            <div style={{ marginTop: 8 }}>
                                                <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
                                                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                                                    适用机场:{" "}
                                                    {(() => {
                                                        const airports = item.applicable_airports || item.product?.default_applicable_airports || [];
                                                        if (airports.length === 0) {
                                                            return "全部";
                                                        }
                                                        return airports.slice(0, 3).join(", ") + (airports.length > 3 ? " 等" : "");
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            ))}
        </div>
    );

    return (
        <>
            <style>{styles}</style>
            <Modal
                title={title}
                open={visible}
                onCancel={onCancel}
                width={1200}
                className="product-selector-modal"
                footer={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            <Text type="secondary">已选择 {Object.keys(selectedProducts).length} 个产品</Text>
                        </div>
                        <Space>
                            <Button onClick={onCancel}>取消</Button>
                            <Button type="primary" onClick={handleConfirm} disabled={Object.keys(selectedProducts).length === 0}>
                                确认选择
                            </Button>
                        </Space>
                    </div>
                }
            >
                {/* 航班上下文信息 */}
                {/* {renderFlightContext()} */}

                {/* 搜索和过滤 */}
                {renderSearchAndFilters()}

                {/* 产品选择区域 */}
                <Card
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>可用产品 ({filteredContractItems.length} 个)</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <TableOutlined style={{ color: viewMode === "table" ? "#1890ff" : "#8c8c8c" }} />
                                <Switch size="small" checked={viewMode === "grouped"} onChange={(checked) => setViewMode(checked ? "grouped" : "table")} />
                                <AppstoreOutlined style={{ color: viewMode === "grouped" ? "#1890ff" : "#8c8c8c" }} />
                            </div>
                        </div>
                    }
                    size="small"
                    styles={{ body: { padding: "12px 16px" } }}
                >
                    {filteredContractItems.length > 0 ? viewMode === "table" ? renderTableView() : renderGroupedView() : <Empty description="暂无可用产品" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                </Card>
            </Modal>
        </>
    );
};

export default ProductSelectorModal;
